/**
 * Clusters Tab — Issue #660
 *
 * Admin UI for managing content clusters (K-means groupings of stories by embedding).
 * Three operational flows:
 * 1. Generate clusters — runs K-means on story embeddings via refresh-clusters edge function
 * 2. Assign stories — maps stories to nearest cluster centroid via assign-story-clusters
 * 3. Generate descriptions — creates AI labels for clusters via gen-cluster-descriptions
 *    (async queue job, tracked via useQueueJob real-time subscription)
 *
 * Inline editing of descriptions is also supported (direct DB update).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { supabase } from '../../../services/supabase';
import { useQueueJob } from '../../../hooks/useQueueJob';
import { PageToolbar } from '../../../shared/components/PageToolbar';

// --- Types ---

interface ClusterRow {
  id: string;
  description: string | null;
  created_at: string;
  storyCount: number;
}

interface ClusterStory {
  slug: string;
  title?: string | null;
  subtitle?: string | null;
  showName?: string | null;
}

// --- Component ---

export const ClustersTab = () => {
  const [clusters, setClusters] = useState<ClusterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [submittingDescriptions, setSubmittingDescriptions] = useState(false);
  const [clusterCount, setClusterCount] = useState(30);

  // Inline description editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState('');

  // Stories dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogStories, setDialogStories] = useState<ClusterStory[]>([]);
  const [dialogLoading, setDialogLoading] = useState(false);

  // Assign mode dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);

  const notify = useNotify();

  // Ref breaks a circular dependency: useQueueJob's onComplete needs fetchData,
  // but fetchData is a useCallback that can't depend on the hook's return value.
  // The ref lets the callback always call the latest fetchData without re-subscribing.
  const fetchDataRef = useRef<() => Promise<void>>();

  const { isWaiting: describingAll, watchJob } = useQueueJob({
    timeout: 3 * 60 * 1000, // 3 minutes
    onComplete: () => {
      notify('Cluster descriptions generated', { type: 'success' });
      fetchDataRef.current?.();
    },
    onError: (err) => notify(`Description generation failed: ${err}`, { type: 'error' }),
    onTimeout: () => notify('Description generation timed out', { type: 'error' }),
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch clusters
      const { data: clusterData, error: clusterErr } = await supabase
        .from('clusters')
        .select('id, description, created_at')
        .order('id');

      if (clusterErr) throw clusterErr;

      if (!clusterData || clusterData.length === 0) {
        setClusters([]);
        return;
      }

      // Compute story counts per cluster via a server-side aggregate. A
      // client-side tally of stories.cluster_id is capped at the PostgREST
      // max_rows (1000) and undercounts the true totals.
      const { data: counts, error: countErr } = await supabase.rpc('get_cluster_story_counts');

      if (countErr) throw countErr;

      const countMap = new Map<string, number>();
      for (const row of counts || []) {
        countMap.set(row.cluster_id as string, Number(row.story_count));
      }

      setClusters(
        clusterData.map(c => ({
          id: c.id,
          description: c.description,
          created_at: c.created_at,
          storyCount: countMap.get(c.id) || 0,
        }))
      );
    } catch (err: any) {
      notify(`Failed to load clusters: ${err.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  fetchDataRef.current = fetchData;

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Handlers ---

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('refresh-clusters', {
        body: { k: clusterCount },
      });
      if (error) throw error;
      if (!data.success) throw new Error(data.error);
      notify(
        `Generated ${data.result.clusterCount} clusters from ${data.result.poolCount} stories`,
        { type: 'success' },
      );
      await fetchData();
    } catch (err: any) {
      notify(`Cluster generation failed: ${err.message}`, { type: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleAssign = async (mode: 'all' | 'unassigned') => {
    setAssignDialogOpen(false);
    setAssigning(true);
    try {
      const { data, error } = await supabase.functions.invoke('assign-story-clusters', {
        body: { mode },
      });
      if (error) throw error;
      if (!data.success) throw new Error(data.error);
      notify(`Assigned ${data.result.assigned} stories (mode: ${mode})`, { type: 'success' });
      await fetchData();
    } catch (err: any) {
      notify(`Assignment failed: ${err.message}`, { type: 'error' });
    } finally {
      setAssigning(false);
    }
  };

  const handleGenerateDescriptions = async () => {
    setSubmittingDescriptions(true);
    try {
      const { data, error } = await supabase.functions.invoke('gen-cluster-descriptions');
      if (error) throw error;
      if (!data.success) throw new Error(data.error);
      watchJob(data.result.jobId);
    } catch (err: any) {
      notify(`Description generation failed: ${err.message}`, { type: 'error' });
    } finally {
      setSubmittingDescriptions(false);
    }
  };

  const handleStoryCountClick = async (cluster: ClusterRow) => {
    setDialogTitle(`Cluster ${cluster.id}: ${cluster.storyCount} stories`);
    setDialogOpen(true);
    setDialogLoading(true);
    setDialogStories([]);

    try {
      // cluster_id is on stories table (not in the enriched view), so query stories
      // first for slugs, then enrich from the view. Both reads are paginated so a
      // cluster larger than PostgREST's max_rows (1000) isn't silently truncated.
      const PAGE = 1000;
      const slugs: string[] = [];
      for (let from = 0; ; from += PAGE) {
        const { data: storyRows, error: slugErr } = await supabase
          .from('stories')
          .select('slug')
          .eq('cluster_id', cluster.id)
          .order('slug', { ascending: true }) // stable order so .range() pages don't skip/overlap
          .range(from, from + PAGE - 1);
        if (slugErr) throw slugErr;
        slugs.push(...(storyRows || []).map(r => r.slug as string));
        if (!storyRows || storyRows.length < PAGE) break;
      }

      if (slugs.length === 0) {
        setDialogStories([]);
      } else {
        // Enrich in chunks: the .in() list and the response are both bounded by
        // max_rows, so batch the slugs and concatenate.
        const enriched: Array<{ slug: string; title: string; subtitle: string; show_name: string }> = [];
        for (let i = 0; i < slugs.length; i += PAGE) {
          const chunk = slugs.slice(i, i + PAGE);
          const { data, error: enrichErr } = await supabase
            .from('stories_admin_enriched')
            .select('slug, title, subtitle, show_name')
            .in('slug', chunk)
            .eq('lang', 'en');
          if (enrichErr) throw enrichErr;
          enriched.push(...(data || []));
        }

        setDialogStories(
          enriched
            .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
            .map(s => ({
              slug: s.slug,
              title: s.title,
              subtitle: s.subtitle,
              showName: s.show_name,
            }))
        );
      }
    } catch (err: any) {
      notify(`Failed to load stories: ${err.message}`, { type: 'error' });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDescriptionClick = (cluster: ClusterRow) => {
    setEditingId(cluster.id);
    setEditingDescription(cluster.description || '');
  };

  const handleDescriptionSave = async () => {
    if (!editingId) return;
    try {
      const { error } = await supabase
        .from('clusters')
        .update({ description: editingDescription || null })
        .eq('id', editingId);
      if (error) throw error;
      setClusters(prev =>
        prev.map(c => (c.id === editingId ? { ...c, description: editingDescription || null } : c))
      );
      setEditingId(null);
    } catch (err: any) {
      notify(`Failed to save description: ${err.message}`, { type: 'error' });
    }
  };

  // --- Meta ---

  const createdAt = clusters[0]?.created_at ?? null;
  const totalStories = clusters.reduce((sum, c) => sum + c.storyCount, 0);
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <PageToolbar left={<Typography variant="h6">Clusters</Typography>} />
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageToolbar
        left={<Typography variant="h6">Clusters</Typography>}
        right={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <InputLabel id="cluster-k-label">Count</InputLabel>
              <Select
                labelId="cluster-k-label"
                label="Count"
                value={clusterCount}
                onChange={(e) => setClusterCount(Number(e.target.value))}
                sx={{ '& .MuiSelect-select': { py: 0.5 } }}
              >
                {[30, 40, 50, 60].map(n => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              onClick={handleGenerate}
              disabled={generating || assigning || submittingDescriptions || describingAll}
            >
              {generating ? 'Generating...' : 'Generate'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setAssignDialogOpen(true)}
              disabled={clusters.length === 0 || generating || assigning || submittingDescriptions || describingAll}
            >
              {assigning ? 'Assigning...' : 'Assign Stories'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleGenerateDescriptions}
              disabled={clusters.length === 0 || generating || assigning || submittingDescriptions || describingAll}
            >
              {submittingDescriptions || describingAll ? 'Generating...' : 'Generate Descriptions'}
            </Button>
          </Box>
        }
      />

      {/* Meta chips */}
      {clusters.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`${clusters.length} clusters`} size="small" variant="outlined" />
          <Chip label={`${totalStories} assigned stories`} size="small" variant="outlined" />
          {createdAt && (
            <Chip
              label={`Generated: ${new Date(createdAt).toLocaleString()}`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      )}

      {clusters.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No clusters yet. Select a cluster count and click &quot;Generate Clusters&quot;.
          </Typography>
        </Card>
      ) : (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={80}>ID</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell width={120} align="right">Stories</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clusters.map(cluster => (
                  <TableRow key={cluster.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {cluster.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {editingId === cluster.id ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            value={editingDescription}
                            onChange={(e) => setEditingDescription(e.target.value)}
                            fullWidth
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleDescriptionSave();
                              if (e.key === 'Escape') setEditingId(null);
                            }}
                          />
                          <Button size="small" onClick={handleDescriptionSave}>Save</Button>
                          <Button size="small" onClick={() => setEditingId(null)}>Cancel</Button>
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          color={cluster.description ? 'text.primary' : 'text.disabled'}
                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => handleDescriptionClick(cluster)}
                        >
                          {cluster.description || '(click to add)'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => handleStoryCountClick(cluster)}
                        sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                      >
                        {cluster.storyCount}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Stories Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent dividers>
          {dialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : dialogStories.length === 0 ? (
            <Typography color="textSecondary" sx={{ p: 2 }}>
              No stories in this cluster.
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={40}>#</TableCell>
                    <TableCell width={80}>&nbsp;</TableCell>
                    <TableCell>Story</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dialogStories.map((s, i) => (
                    <TableRow key={s.slug} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <img
                          src={`${apiBaseUrl}/drops/${s.slug}/image?size=50`}
                          alt=""
                          width={50}
                          height={50}
                          style={{ borderRadius: 4, objectFit: 'cover' }}
                        />
                      </TableCell>
                      <TableCell>
                        {s.showName && (
                          <Typography variant="caption" color="text.secondary" display="block" noWrap>
                            {s.showName}
                          </Typography>
                        )}
                        <Typography variant="body2" noWrap>{s.title || s.slug}</Typography>
                        {s.subtitle && (
                          <Typography variant="caption" color="text.secondary" display="block" noWrap>
                            {s.subtitle}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Mode Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)}>
        <DialogTitle>Assign Stories to Clusters</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose which stories to assign to their nearest cluster centroid:
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => handleAssign('unassigned')} variant="outlined">
            Unassigned Only
          </Button>
          <Button onClick={() => handleAssign('all')} variant="contained">
            All Stories
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
